// Sales hub — one outlet panel with four tabs (desktop Sales menu parity):
//   Point of Sales | Receipt | On Hold Receipt | Summary
//
// Composes the shared trade children:
//   <pos-entry mode="cash">                 — POS entry (SaveSales)
//   <sales-records kind="sales|hold" mode="cash">
//   <summary-report kind="sales-cash">
//
// Recall/Edit mediation: the lists dispatch "trade-recall" (holds) or
// "trade-edit" (posted receipts); the hub switches to the POS tab and hands
// the record to pos-entry. A recalled hold is deleted as soon as it loads
// (desktop parity, so no other terminal can recall it), retried on save if
// that call failed; an edited receipt is saved back to the same id.

import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { TradeSalesRow } from '../lib/api.js';
import { OutletViewCache } from '../lib/outlet-view-cache.js';
import { TAB_UI_STYLES } from '../styles/tab-ui.js';
import './pos-entry.js';
import './sales-records.js';
import './summary-report.js';

type SalesTab = 'pos' | 'receipt' | 'hold' | 'summary';

interface KeptSalesView {
  tab: SalesTab;
}

@customElement('sales-page')
export class SalesPage extends LitElement {
  static styles = [TAB_UI_STYLES, css`
    :host { display: flex; flex-direction: column; height: 100%; min-height: 0; overflow: hidden; }
    .tabs { margin: 0.75rem 1rem 0; margin-bottom: 0; }
    .pane { flex: 1; min-height: 0; display: flex; flex-direction: column; }
    .pane > * { flex: 1; min-height: 0; }
  `];

  @property({ type: String }) storeCode = '';

  @state() private _tab: SalesTab = 'pos';
  @state() private _recall: { row: TradeSalesRow; token: number; edit?: boolean } | null = null;
  private _recallSeq = 0;

  private _view = new OutletViewCache<KeptSalesView>(this, 'sales-page', {
    // The recall is deliberately NOT persisted: pos-entry keeps its own
    // per-outlet view (lines + edit id), and re-applying a stale recall would
    // overwrite that restored screen.
    capture: () => ({ tab: this._tab }),
    restore: (v) => {
      this._tab = v.tab;
      this._recall = null;
    },
    reset: () => {
      this._tab = 'pos';
      this._recall = null;
    },
  });

  disconnectedCallback() {
    super.disconnectedCallback();
    this._view.dispose();
  }

  protected updated() {
    this._view.switchTo(this.storeCode);
  }

  private _onRecall = (e: Event) => {
    const detail = (e as CustomEvent).detail as
      { tradeKind: string; row: TradeSalesRow; edit?: boolean } | undefined;
    if (!detail || detail.tradeKind !== 'sale' || !detail.row) return;
    this._recallSeq += 1;
    this._recall = { row: detail.row, token: this._recallSeq, edit: !!detail.edit };
    this._tab = 'pos';
  };

  private _tabLabel(id: SalesTab): string {
    switch (id) {
      case 'pos': return 'Point of Sales';
      case 'receipt': return 'Receipt';
      case 'hold': return 'On Hold Receipt';
      default: return 'Summary';
    }
  }

  render() {
    if (!this.storeCode) return html`<div style="padding:2rem;color:var(--color-text-secondary)">No outlet selected.</div>`;
    const tabs: SalesTab[] = ['pos', 'receipt', 'hold', 'summary'];
    return html`
      <div class="tabs">
        ${tabs.map((t) => html`
          <button class=${this._tab === t ? 'active' : ''}            data-testid="sales-tab" data-tab=${t}            @click=${() => { if (t !== 'pos') this._recall = null; this._tab = t; }}>
            ${this._tabLabel(t)}
          </button>
        `)}
      </div>
      <div class="pane" @trade-recall=${this._onRecall} @trade-edit=${this._onRecall}>
        ${this._tab === 'pos' ? html`
          <pos-entry
            .storeCode=${this.storeCode}
            .mode=${'cash'}
            .recallRow=${this._recall?.row ?? null}
            .recallToken=${this._recall?.token ?? 0}
            .editMode=${this._recall?.edit ?? false}
          ></pos-entry>
        ` : ''}
        ${this._tab === 'receipt' ? html`
          <sales-records .storeCode=${this.storeCode} .kind=${'sales'} .mode=${'cash'}></sales-records>
        ` : ''}
        ${this._tab === 'hold' ? html`
          <sales-records .storeCode=${this.storeCode} .kind=${'hold'} .mode=${'cash'}></sales-records>
        ` : ''}
        ${this._tab === 'summary' ? html`
          <summary-report .storeCode=${this.storeCode} .kind=${'sales-cash'}></summary-report>
        ` : ''}
      </div>
    `;
  }
}
